"use client";
import { InstantSearch, Configure, useHits } from "react-instantsearch";
import { useMemo } from "react";
import getPublicClient from "@/lib/algolia/getPublicClient";
import getFilters from "@/components/Search/utils/getFilters";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import ProductCompare from "@/components/ProductCompare";
import type { ProductCompareMonarchProps } from "@/monarch/types/ProductCompare";
import {
  buildOverlapFilters,
  getCompareFilterProductKey,
  getPartnerNamesKey,
  getRelatedProductsFiltersKey,
  resolveRelatedProductsFilters,
  type CompareFilterProduct,
} from "@/components/ProductCompare/buildCompareFacetFilters";

interface ProductCompareWithPartnerProgramsProps {
  productCompare: ProductCompareMonarchProps;
  currentUuid: string;
  partnerNames: string[];
  product?: CompareFilterProduct;
  currentCourseCheckoutUrl?: string | null;
  location?: string;
  hasStickyOffset?: "all" | "mobile-only";
  currentCtaLabel?: string;
}

function ProductCompareInner({
  productCompare,
  currentUuid,
  currentCourseCheckoutUrl,
  location,
  hasStickyOffset,
  currentCtaLabel,
}: Omit<ProductCompareWithPartnerProgramsProps, "partnerNames" | "product">) {
  const { items: hits } = useHits<{ uuid?: string; objectID: string }>();

  const normalizeUuid = (raw: string) => raw.replace(/^program-/, "");
  const normalizedCurrentUuid = normalizeUuid(currentUuid);

  const fallbackUuids = hits
    .map((h) => normalizeUuid(h.uuid ?? h.objectID))
    .filter((uuid): uuid is string => !!uuid && uuid !== normalizedCurrentUuid);

  const comparisonUuids = productCompare.uuids?.length
    ? productCompare.uuids
    : fallbackUuids;
  if (comparisonUuids.length === 0) return null;

  const uuids = [normalizedCurrentUuid, ...comparisonUuids];

  return (
    <ProductCompare
      productCompare={productCompare}
      productUuids={uuids}
      currentCourseCheckoutUrl={currentCourseCheckoutUrl}
      location={location}
      hasStickyOffset={hasStickyOffset}
      currentCtaLabel={currentCtaLabel}
    />
  );
}

export default function ProductCompareWithPartnerPrograms({
  productCompare,
  currentUuid,
  partnerNames,
  product,
  currentCourseCheckoutUrl,
  location,
  hasStickyOffset,
  currentCtaLabel,
}: ProductCompareWithPartnerProgramsProps) {
  const searchClient = getPublicClient();

  // Content-based keys — templates recreate arrays/objects every render
  // (partnerNames.map, inline product={{...}}), which would otherwise make
  // <Configure> props change every render and re-fire Algolia queries.
  const enabledFiltersKey = getRelatedProductsFiltersKey(
    productCompare.relatedProductsFilters,
    "program",
  );
  const partnerNamesKey = getPartnerNamesKey(partnerNames);
  const productKey = getCompareFilterProductKey(product);

  const configForSearch = useMemo(() => {
    const enabledFilters = resolveRelatedProductsFilters(
      productCompare.relatedProductsFilters,
      "program",
    );
    const overlapFilters = buildOverlapFilters(enabledFilters, product);
    const facetFilters =
      overlapFilters.length > 0 ? [overlapFilters] : undefined;

    return {
      filters: getFilters({ productType: "program" }),
      disjunctiveFacets: enabledFilters.includes("sameBrand")
        ? ["partner"]
        : [],
      disjunctiveFacetsRefinements: enabledFilters.includes("sameBrand")
        ? { partner: partnerNames }
        : undefined,
      facetFilters,
      facets: ["product"],
      clickAnalytics: false,
      facetsRefinements: { product: ["program"] },
      hitsPerPage: 4,
    };
    // Keys capture filter/product/partner content; values are read from closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFiltersKey, partnerNamesKey, productKey]);

  return (
    <InstantSearch searchClient={searchClient} indexName={PRODUCT_INDEX_EN}>
      <Configure {...configForSearch} />
      <ProductCompareInner
        productCompare={productCompare}
        currentUuid={currentUuid}
        currentCourseCheckoutUrl={currentCourseCheckoutUrl}
        location={location}
        hasStickyOffset={hasStickyOffset}
        currentCtaLabel={currentCtaLabel}
      />
    </InstantSearch>
  );
}
